import React from "react";
import { useTranslation } from "react-i18next";
import { Scale } from "lucide-react";
import { DateRangePicker } from "@/components/common/DateRangePicker";
import { Button } from "@/components/ui/button";
import { AnalyticsExportMenu } from "../shared/AnalyticsExportMenu";
import type { DateRange } from "@/hooks/useDateRange";
import { cn } from "@/lib/utils";
import { getAnalyticsMaxSelectableDate } from "../../utils/analyticsDateUtils";
import { ANALYTICS_TOOLBAR_BUTTON_CLASS } from "../shared/toolbarButtonStyles";

interface ServiceDetailFiltersBarProps {
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  onExport?: (format: "csv" | "xlsx") => void;
  isExportDisabled?: boolean;
}

export const ServiceDetailFiltersBar: React.FC<
  ServiceDetailFiltersBarProps
> = ({ dateRange, onDateRangeChange, onExport, isExportDisabled = false }) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-white py-2">
      <div className="flex flex-wrap items-center gap-2">
        <DateRangePicker
          dateRange={dateRange}
          onDateRangeChange={onDateRangeChange}
          maxDate={getAnalyticsMaxSelectableDate()}
          className="min-h-8 rounded-md border border-border bg-white px-3 py-1.5 text-sm"
        />
        <Button
          type="button"
          variant="span"
          disabled
          title={t(
            "analytics.common.comingSoon.compare",
            "Compare will be available with backend data",
          )}
          className={ANALYTICS_TOOLBAR_BUTTON_CLASS}
        >
          <Scale className="size-3.5" />
          {t("analytics.common.compare", "Compare")}
        </Button>
      </div>

      <AnalyticsExportMenu
        onExport={(format) => onExport?.(format)}
        disabled={isExportDisabled || !onExport}
        className={cn(ANALYTICS_TOOLBAR_BUTTON_CLASS, "disabled:opacity-50")}
      />
    </div>
  );
};
